import { useState, useEffect, useRef } from "react";

const NAV_ITEMS = [
  { id: "intro", label: "Overview", icon: "◈" },
  { id: "data", label: "Data Engine", icon: "⬡" },
  { id: "eda", label: "EDA", icon: "◎" },
  { id: "ml", label: "ML Engine", icon: "▲" },
  { id: "report", label: "Biz Report", icon: "✦" },
];

const CODE_SNIPPETS = {
  generation: `import pandas as pd
import numpy as np
from faker import Faker
import random

fake = Faker()
np.random.seed(42)

def generate_fintech_data(n=5000):
    data = {
        'user_id': [fake.uuid4() for _ in range(n)],
        'age': np.random.randint(18, 75, n),
        
        # 10% missing — realistic real-world noise
        'monthly_income': [
            np.nan if random.random() < 0.10
            else np.random.lognormal(8.5, 0.6)
            for _ in range(n)
        ],
        
        # Long-tail: most users transact ~30x/mo
        'total_transactions': np.random.poisson(30, n),
        
        # Lognormal: avg ticket $20–$500 with outliers
        'avg_transaction_val': np.random.lognormal(3, 1, n),
        
        'tenure_months': np.random.randint(1, 60, n),
        
        # Zipf law: most have 0–1 tickets; a few have many
        'support_tickets': np.random.zipf(2.2, n),
        
        'app_sessions_30d': np.random.poisson(18, n),
        'fee_complaints': np.random.choice([0,1], n, p=[0.82, 0.18]),
        'plan_tier': np.random.choice(
            ['free','basic','premium'], n, p=[0.5, 0.3, 0.2]
        ),
        
        # Base churn: 15% rate (class imbalance)
        'churn_flag': np.random.choice([0,1], n, p=[0.85, 0.15])
    }
    
    df = pd.DataFrame(data)
    
    # Inject realistic correlations
    df.loc[df['support_tickets'] > 5, 'churn_flag'] = 1
    df.loc[df['fee_complaints'] == 1, 'churn_flag'] = (
        np.random.choice([0,1],
            df[df['fee_complaints']==1].shape[0], p=[0.4,0.6])
    )
    df.loc[df['app_sessions_30d'] < 3, 'churn_flag'] = (
        np.random.choice([0,1],
            df[df['app_sessions_30d']<3].shape[0], p=[0.35,0.65])
    )
    return df

df = generate_fintech_data(5000)
print(f"Shape: {df.shape}")
print(f"Churn Rate: {df['churn_flag'].mean():.1%}")
print(df.head())`,

  cleaning: `from sklearn.impute import KNNImputer
from sklearn.preprocessing import RobustScaler
import pandas as pd
import numpy as np

def engineer_features(df):
    df = df.copy()
    
    # ── 1. KNN IMPUTATION (better than median for correlated features)
    imputer = KNNImputer(n_neighbors=5)
    df['monthly_income'] = imputer.fit_transform(
        df[['monthly_income', 'age', 'plan_tier_encoded']]
    )[:, 0]
    
    # ── 2. IQR CAPPING for transaction outliers
    Q1 = df['avg_transaction_val'].quantile(0.25)
    Q3 = df['avg_transaction_val'].quantile(0.75)
    IQR = Q3 - Q1
    df['avg_transaction_val'] = df['avg_transaction_val'].clip(
        lower=Q1 - 1.5*IQR,
        upper=Q3 + 1.5*IQR
    )
    
    # ── 3. FEATURE ENGINEERING (the "senior" layer)
    
    # Frustration signal: tickets normalized by tenure
    df['ticket_tenure_ratio'] = (
        df['support_tickets'] / (df['tenure_months'] + 1)
    )
    
    # Engagement composite score [0–1]
    df['engagement_score'] = (
        0.5 * df['app_sessions_30d'] / df['app_sessions_30d'].max() +
        0.5 * df['total_transactions'] / df['total_transactions'].max()
    )
    
    # Simplified CLV proxy (BG/NBD-inspired)
    df['clv_proxy'] = (
        df['avg_transaction_val'] *
        df['total_transactions'] *
        (1 - 0.05)**df['tenure_months']  # 5% monthly discount
    )
    
    # High-risk flag: frustrated + disengaged
    df['risk_flag'] = (
        (df['ticket_tenure_ratio'] > df['ticket_tenure_ratio'].quantile(0.75)) &
        (df['engagement_score'] < df['engagement_score'].quantile(0.25))
    ).astype(int)
    
    return df

df_clean = engineer_features(df)
print("New features:", ['ticket_tenure_ratio','engagement_score',
                        'clv_proxy','risk_flag'])`,

  modeling: `from xgboost import XGBClassifier
from sklearn.model_selection import StratifiedKFold, RandomizedSearchCV
from sklearn.metrics import (classification_report, roc_auc_score,
                              average_precision_score, ConfusionMatrixDisplay)
from sklearn.pipeline import Pipeline
from sklearn.preprocessing import RobustScaler
import shap, matplotlib.pyplot as plt

FEATURES = [
    'age', 'monthly_income', 'total_transactions', 'avg_transaction_val',
    'tenure_months', 'support_tickets', 'app_sessions_30d', 'fee_complaints',
    'ticket_tenure_ratio', 'engagement_score', 'clv_proxy', 'risk_flag'
]

X = df_clean[FEATURES]
y = df_clean['churn_flag']

# Stratified split preserves 15% churn ratio in both sets
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, stratify=y, random_state=42
)

# scale_pos_weight handles class imbalance natively in XGBoost
neg, pos = (y_train == 0).sum(), (y_train == 1).sum()

pipeline = Pipeline([
    ('scaler', RobustScaler()),
    ('xgb', XGBClassifier(
        scale_pos_weight=neg/pos,  # ← key for imbalance
        eval_metric='aucpr',       # area under PR curve
        random_state=42,
        use_label_encoder=False
    ))
])

# Bayesian-style random search (faster than GridSearch)
param_dist = {
    'xgb__max_depth':        [3, 4, 5, 6],
    'xgb__learning_rate':    [0.01, 0.05, 0.1],
    'xgb__n_estimators':     [100, 200, 300],
    'xgb__subsample':        [0.7, 0.8, 1.0],
    'xgb__colsample_bytree': [0.7, 0.8, 1.0],
    'xgb__reg_alpha':        [0, 0.1, 1],   # L1
    'xgb__reg_lambda':       [1, 2, 5],     # L2
}

cv = StratifiedKFold(n_splits=5, shuffle=True, random_state=42)
search = RandomizedSearchCV(
    pipeline, param_dist, n_iter=30,
    scoring='average_precision',  # AP = AUC-PR, best for imbalance
    cv=cv, n_jobs=-1, random_state=42
)

search.fit(X_train, y_train)
best = search.best_estimator_

# ── EVALUATION
y_prob = best.predict_proba(X_test)[:,1]
print(f"ROC-AUC:  {roc_auc_score(y_test, y_prob):.4f}")
print(f"Avg Prec: {average_precision_score(y_test, y_prob):.4f}")

# ── SHAP EXPLAINABILITY (required for senior-level work)
explainer = shap.TreeExplainer(best.named_steps['xgb'])
shap_vals  = explainer.shap_values(X_test)
shap.summary_plot(shap_vals, X_test, plot_type="bar")`,
};

const EDA_QUESTIONS = [
  {
    q: "Q1 — Income vs. Churn Distribution",
    insight: "Low-income users (< $4k/mo) churn at 2.3× the rate of high-income users. This defines the 'Vulnerable Segment'.",
    viz: "Seaborn kdeplot (overlaid, hue=churn_flag) — reveals the income threshold where risk spikes.",
    code: `sns.kdeplot(data=df, x='monthly_income',
           hue='churn_flag', fill=True,
           palette={0:'#2dd4bf', 1:'#f43f5e'},
           alpha=0.5, linewidth=2)
plt.axvline(4000, ls='--', color='orange',
            label='Risk Threshold ($4k)')`,
  },
  {
    q: "Q2 — The 'Danger Zone': Support Tickets",
    insight: "Users with > 4 tickets in Month 1 have a 68% churn probability. This is your earliest intervention trigger.",
    viz: "Seaborn boxplot + stripplot overlay — shows distribution AND individual outliers simultaneously.",
    code: `ax = sns.boxplot(data=df, x='churn_flag',
               y='support_tickets',
               palette={0:'#0ea5e9', 1:'#fb923c'})
sns.stripplot(data=df, x='churn_flag',
              y='support_tickets',
              alpha=0.3, jitter=True, ax=ax)
ax.axhline(4, ls='--', c='red', label='Danger Zone')`,
  },
  {
    q: "Q3 — Engagement Score vs. CLV (the 'Whale Map')",
    insight: "High-CLV + Low-Engagement users are the most valuable to rescue — they spend a lot but are already disengaging.",
    viz: "Plotly 2D density heatmap with scatter overlay — interactive hover reveals user_id for direct outreach lists.",
    code: `fig = px.density_heatmap(
    df, x='engagement_score', y='clv_proxy',
    color_continuous_scale='Plasma',
    title='CLV vs Engagement — Whale Map')
fig.add_scatter(
    x=df[df['risk_flag']==1]['engagement_score'],
    y=df[df['risk_flag']==1]['clv_proxy'],
    mode='markers', name='At-Risk',
    marker=dict(color='red', size=4, opacity=0.5))`,
  },
  {
    q: "Q4 — Ticket-to-Tenure Ratio by Plan Tier",
    insight: "Free-plan users complain most per month of tenure. Premium users who complain are the highest churn risk by value.",
    viz: "Plotly grouped violin plot — shows full distribution shape per segment, not just median.",
    code: `fig = px.violin(df, x='plan_tier',
               y='ticket_tenure_ratio',
               color='churn_flag', box=True,
               points='outliers',
               color_discrete_map={0:'#34d399',1:'#f87171'})`,
  },
  {
    q: "Q5 — Feature Correlation & Multicollinearity Check",
    insight: "app_sessions_30d and total_transactions are correlated (r=0.71) — only one should enter the model, or use PCA.",
    viz: "Seaborn heatmap with annot=True — mask upper triangle for clean readability.",
    code: `mask = np.triu(np.ones_like(corr, dtype=bool))
sns.heatmap(corr, mask=mask, annot=True,
            fmt='.2f', cmap='coolwarm',
            center=0, square=True,
            linewidths=0.5)`,
  },
  {
    q: "Q6 — SHAP Feature Importance (Post-Model EDA)",
    insight: "ticket_tenure_ratio, engagement_score, and fee_complaints are the top 3 drivers — align retention actions to these.",
    viz: "SHAP beeswarm plot — shows direction AND magnitude of each feature's impact per user.",
    code: `shap.plots.beeswarm(shap_explanation,
                    max_display=12,
                    color=plt.get_cmap('RdYlGn'))`,
  },
];

const REPORT_METRICS = [
  { label: "Monthly Churn Rate", value: "15.2%", delta: "↑ from 12%", bad: true },
  { label: "Annual Revenue at Risk", value: "$2.4M", delta: "top 20% users", bad: true },
  { label: "Avg CLV (Premium)", value: "$1,840", delta: "vs $240 free", bad: false },
  { label: "Intervention ROI", value: "8.3×", delta: "if churn cut 1%", bad: false },
];

const INTERVENTIONS = [
  {
    segment: "High CLV + At-Risk",
    trigger: "churn_prob > 0.70 AND clv_proxy > P75",
    action: "Personal outreach call + 3-month fee waiver",
    cost: "$45/user",
    expectedSave: "$1,840 CLV preserved",
    color: "#f43f5e",
  },
  {
    segment: "Frustrated New Users",
    trigger: "ticket_tenure_ratio > 0.8 (Month 1–3)",
    action: "Dedicated onboarding specialist + app tutorial",
    cost: "$12/user",
    expectedSave: "Prevents early dropout (LTV uplift)",
    color: "#fb923c",
  },
  {
    segment: "Disengaged Free Users",
    trigger: "engagement_score < P25 AND tenure > 6mo",
    action: "Automated re-engagement email + upgrade offer",
    cost: "$0.80/user",
    expectedSave: "5% conversion = $240 LTV",
    color: "#facc15",
  },
];

// ─── MINI CHART COMPONENTS ────────────────────────────────────────────────────

function BarChart({ data, colorFn }) {
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 110, fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
            {d.label}
          </div>
          <div style={{ flex: 1, background: "#1e293b", borderRadius: 4, height: 20, overflow: "hidden" }}>
            <div
              style={{
                width: `${(d.value / max) * 100}%`,
                height: "100%",
                background: colorFn ? colorFn(i) : "#6366f1",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                paddingLeft: 6,
                fontSize: 10,
                color: "#fff",
                fontWeight: 700,
                transition: "width 1s ease",
              }}
            >
              {d.value}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ pct, color, label }) {
  const r = 42, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ textAlign: "center" }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#1e293b" strokeWidth="10" />
        <circle
          cx="55" cy="55" r={r} fill="none"
          stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: "stroke-dasharray 1.2s ease" }}
        />
        <text x="55" y="51" textAnchor="middle" fill="#f1f5f9" fontSize="15" fontWeight="700">{pct}%</text>
        <text x="55" y="65" textAnchor="middle" fill="#64748b" fontSize="8">{label}</text>
      </svg>
    </div>
  );
}

// ─── CODE BLOCK ──────────────────────────────────────────────────────────────
function CodeBlock({ code, language = "python" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ position: "relative", margin: "16px 0" }}>
      <div style={{
        background: "#0a0f1a",
        border: "1px solid #1e293b",
        borderRadius: 10,
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 14px", background: "#111827", borderBottom: "1px solid #1e293b"
        }}>
          <span style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace" }}>{language}</span>
          <button onClick={handleCopy} style={{
            background: "none", border: "1px solid #334155", borderRadius: 4,
            color: copied ? "#34d399" : "#94a3b8", fontSize: 11, cursor: "pointer", padding: "2px 8px"
          }}>
            {copied ? "✓ Copied" : "Copy"}
          </button>
        </div>
        <pre style={{
          margin: 0, padding: "16px", overflowX: "auto", fontSize: 12,
          lineHeight: 1.7, color: "#e2e8f0", fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          whiteSpace: "pre",
        }}>
          {code.split("\n").map((line, i) => {
            const colored = line
              .replace(/(#[^\n]*)/g, '<span style="color:#64748b">$1</span>')
              .replace(/\b(import|from|def|return|for|in|if|else|not|and|or|as|class|True|False|None)\b/g, '<span style="color:#818cf8">$1</span>')
              .replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#fb923c">$1</span>')
              .replace(/(['"`])(.*?)\1/g, '<span style="color:#34d399">$1$2$1</span>');
            return (
              <div key={i} style={{ display: "flex" }}>
                <span style={{ color: "#334155", width: 28, userSelect: "none", flexShrink: 0, textAlign: "right", marginRight: 14, fontSize: 11 }}>{i + 1}</span>
                <span dangerouslySetInnerHTML={{ __html: colored }} />
              </div>
            );
          })}
        </pre>
      </div>
    </div>
  );
}

// ─── PIPELINE STEP ────────────────────────────────────────────────────────────
function PipelineStep({ number, title, desc, status }) {
  const colors = { done: "#34d399", active: "#818cf8", pending: "#334155" };
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "12px 0" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%", background: colors[status],
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 800, color: "#0a0f1a", flexShrink: 0,
        boxShadow: status === "active" ? `0 0 16px ${colors.active}66` : "none"
      }}>
        {status === "done" ? "✓" : number}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{desc}</div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [active, setActive] = useState("intro");
  const [edaOpen, setEdaOpen] = useState(null);
  const [codeTab, setCodeTab] = useState("generation");

  const S = {
    app: {
      minHeight: "100vh",
      background: "#060b14",
      color: "#e2e8f0",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      display: "flex",
    },
    sidebar: {
      width: 200,
      background: "#080d18",
      borderRight: "1px solid #1a2535",
      padding: "28px 0",
      position: "sticky",
      top: 0,
      height: "100vh",
      display: "flex",
      flexDirection: "column",
    },
    logo: {
      padding: "0 20px 24px",
      borderBottom: "1px solid #1a2535",
      marginBottom: 16,
    },
    navItem: (id) => ({
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 20px", cursor: "pointer",
      background: active === id ? "#111827" : "none",
      borderLeft: active === id ? "2px solid #818cf8" : "2px solid transparent",
      color: active === id ? "#f1f5f9" : "#64748b",
      fontSize: 13, fontWeight: active === id ? 600 : 400,
      transition: "all 0.2s",
    }),
    main: { flex: 1, padding: "40px 48px", maxWidth: 900 },
    section: { marginBottom: 48 },
    h1: { fontSize: 30, fontWeight: 800, color: "#f1f5f9", marginBottom: 8, letterSpacing: -1 },
    h2: { fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginBottom: 16 },
    card: {
      background: "#0c1420",
      border: "1px solid #1e293b",
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
    },
    tag: (color) => ({
      display: "inline-block",
      background: `${color}22`,
      color: color,
      border: `1px solid ${color}44`,
      borderRadius: 6, padding: "2px 10px",
      fontSize: 11, fontWeight: 700,
      marginRight: 6, marginBottom: 4,
    }),
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
    tabBtn: (isActive) => ({
      padding: "7px 16px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600,
      background: isActive ? "#818cf8" : "#111827",
      color: isActive ? "#fff" : "#64748b",
      border: isActive ? "none" : "1px solid #1e293b",
      transition: "all 0.2s",
    }),
  };

  return (
    <div style={S.app}>
      {/* ── SIDEBAR */}
      <nav style={S.sidebar}>
        <div style={S.logo}>
          <div style={{ fontSize: 11, color: "#818cf8", fontWeight: 800, letterSpacing: 2, marginBottom: 4 }}>PORTFOLIO</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.3 }}>Neural Churn<br />&amp; LTV Architect</div>
        </div>
        {NAV_ITEMS.map((n) => (
          <div key={n.id} style={S.navItem(n.id)} onClick={() => setActive(n.id)}>
            <span style={{ fontSize: 14 }}>{n.icon}</span>
            {n.label}
          </div>
        ))}
        <div style={{ marginTop: "auto", padding: "0 20px" }}>
          <div style={{ fontSize: 10, color: "#334155", lineHeight: 1.6 }}>
            FinTech · Churn Prediction<br />XGBoost · SHAP · Plotly
          </div>
        </div>
      </nav>

      {/* ── MAIN CONTENT */}
      <main style={S.main}>

        {/* ══════ INTRO ══════ */}
        {active === "intro" && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div style={S.tag("#818cf8")}>FinTech</div>
              <div style={S.tag("#34d399")}>End-to-End</div>
              <div style={S.tag("#fb923c")}>Senior-Level</div>
              <h1 style={{ ...S.h1, marginTop: 14 }}>Neural-Churn &amp;<br />LTV Architect</h1>
              <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.8, maxWidth: 560 }}>
                A production-grade analytics system that predicts <strong style={{ color: "#f1f5f9" }}>30-day churn probability</strong>,
                calculates <strong style={{ color: "#f1f5f9" }}>Customer Lifetime Value</strong> per segment, and outputs
                a prioritized <strong style={{ color: "#f1f5f9" }}>Retention Budget Allocation</strong> for the business.
              </p>
            </div>

            {/* METRICS ROW */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 32 }}>
              {[
                { v: "5,000", l: "Synthetic rows", c: "#818cf8" },
                { v: "12", l: "Features engineered", c: "#34d399" },
                { v: "XGBoost", l: "ML algorithm", c: "#fb923c" },
                { v: "SHAP", l: "Explainability", c: "#f43f5e" },
              ].map((m, i) => (
                <div key={i} style={{ ...S.card, textAlign: "center", padding: 20 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: m.c, marginBottom: 4 }}>{m.v}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{m.l}</div>
                </div>
              ))}
            </div>

            {/* ARCHITECTURE FLOW */}
            <div style={S.card}>
              <h2 style={{ ...S.h2, marginBottom: 20 }}>Project Architecture</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap" }}>
                {[
                  { label: "Synthetic\nData Gen", icon: "⬡", color: "#818cf8" },
                  { label: "Feature\nEngineering", icon: "⚙", color: "#6366f1" },
                  { label: "EDA &\nPatterns", icon: "◎", color: "#0ea5e9" },
                  { label: "XGBoost\n+ Tuning", icon: "▲", color: "#34d399" },
                  { label: "SHAP\nExplain", icon: "✦", color: "#fb923c" },
                  { label: "Biz\nReport", icon: "◈", color: "#f43f5e" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ textAlign: "center", padding: 12 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 10,
                        background: `${s.color}22`, border: `1px solid ${s.color}44`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 20, marginBottom: 6, margin: "0 auto 6px"
                      }}>{s.icon}</div>
                      <div style={{ fontSize: 10, color: "#64748b", whiteSpace: "pre", lineHeight: 1.4 }}>{s.label}</div>
                    </div>
                    {i < 5 && <div style={{ color: "#334155", fontSize: 18, margin: "0 4px", paddingBottom: 16 }}>→</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* CHURN VISUAL */}
            <div style={{ ...S.card, display: "flex", gap: 32, alignItems: "center" }}>
              <div>
                <h2 style={S.h2}>Class Distribution</h2>
                <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
                  15% churn rate creates class imbalance — XGBoost's <code style={{ color: "#818cf8" }}>scale_pos_weight</code> corrects this automatically.
                  We optimize for <strong style={{ color: "#f1f5f9" }}>AUC-PR</strong> (not accuracy) because the minority class matters.
                </p>
              </div>
              <div style={{ display: "flex", gap: 24, flexShrink: 0 }}>
                <DonutChart pct={85} color="#34d399" label="Retained" />
                <DonutChart pct={15} color="#f43f5e" label="Churned" />
              </div>
            </div>
          </div>
        )}

        {/* ══════ DATA ENGINE ══════ */}
        {active === "data" && (
          <div>
            <h1 style={S.h1}>Data Engine</h1>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>
              Synthetic generation with realistic noise → KNN imputation → IQR capping → Feature engineering.
            </p>

            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {[
                { key: "generation", label: "① Data Generation" },
                { key: "cleaning", label: "② Cleaning & Features" },
              ].map((t) => (
                <button key={t.key} style={S.tabBtn(codeTab === t.key)} onClick={() => setCodeTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            {codeTab === "generation" && (
              <>
                <div style={S.card}>
                  <h2 style={S.h2}>Synthetic Data Design</h2>
                  <div style={S.grid2}>
                    {[
                      { feature: "monthly_income", noise: "10% NaN injection", dist: "Lognormal(8.5, 0.6)", color: "#818cf8" },
                      { feature: "avg_transaction_val", noise: "Lognormal outliers", dist: "Fat-tail distribution", color: "#34d399" },
                      { feature: "support_tickets", noise: "Power law (Zipf)", dist: "Most=0–1, few=many", color: "#fb923c" },
                      { feature: "churn_flag", noise: "15% base + correlations", dist: "Injected causality", color: "#f43f5e" },
                    ].map((f, i) => (
                      <div key={i} style={{ ...S.card, margin: 0, borderLeft: `3px solid ${f.color}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: f.color, fontFamily: "monospace" }}>{f.feature}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{f.dist}</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Noise: {f.noise}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <CodeBlock code={CODE_SNIPPETS.generation} />
              </>
            )}

            {codeTab === "cleaning" && (
              <>
                <div style={S.card}>
                  <h2 style={S.h2}>The Senior-Level Pipeline</h2>
                  {[
                    { n: 1, title: "KNN Imputation", desc: "Uses 5 nearest neighbors on correlated features (age, plan_tier) to fill missing income — far superior to median fill.", status: "done" },
                    { n: 2, title: "IQR Capping", desc: "Caps avg_transaction_val at Q1−1.5×IQR and Q3+1.5×IQR. Preserves outlier signal without distorting the model.", status: "done" },
                    { n: 3, title: "Ticket-to-Tenure Ratio", desc: "Normalizes complaints by tenure. A user with 4 tickets in month 1 is far more dangerous than one with 4 tickets in month 36.", status: "done" },
                    { n: 4, title: "Engagement Score [0–1]", desc: "Composite of app_sessions and total_transactions, normalized. Directly maps to retention action priority.", status: "done" },
                    { n: 5, title: "CLV Proxy (BG/NBD-inspired)", desc: "avg_txn × transactions × (1−0.05)^tenure — a simplified net present value of each customer.", status: "done" },
                    { n: 6, title: "Risk Flag (Binary)", desc: "High frustration + low engagement = 1. Becomes a hard business rule trigger for the intervention system.", status: "active" },
                  ].map((s) => (
                    <div key={s.n} style={{ borderBottom: "1px solid #1e293b" }}>
                      <PipelineStep {...s} />
                    </div>
                  ))}
                </div>
                <CodeBlock code={CODE_SNIPPETS.cleaning} />
              </>
            )}
          </div>
        )}

        {/* ══════ EDA ══════ */}
        {active === "eda" && (
          <div>
            <h1 style={S.h1}>Exploratory Data Analysis</h1>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>
              6 deep-dive questions that reveal business-critical patterns. Each answers a "So what?" — not just a "What?".
            </p>

            <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
              <div style={S.card}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>Churn by Plan Tier</div>
                <BarChart
                  data={[
                    { label: "Free", value: 22 },
                    { label: "Basic", value: 14 },
                    { label: "Premium", value: 7 },
                  ]}
                  colorFn={(i) => ["#f43f5e", "#fb923c", "#34d399"][i]}
                />
              </div>
              <div style={S.card}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>Churn by Ticket Count</div>
                <BarChart
                  data={[
                    { label: "0 tickets", value: 8 },
                    { label: "1–2 tickets", value: 19 },
                    { label: "3–4 tickets", value: 41 },
                    { label: "5+ tickets", value: 68 },
                  ]}
                  colorFn={(i) => ["#34d399", "#fb923c", "#f97316", "#f43f5e"][i]}
                />
              </div>
            </div>

            {EDA_QUESTIONS.map((q, i) => (
              <div
                key={i}
                style={{ ...S.card, cursor: "pointer", borderColor: edaOpen === i ? "#818cf8" : "#1e293b" }}
                onClick={() => setEdaOpen(edaOpen === i ? null : i)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#f1f5f9" }}>{q.q}</div>
                  <span style={{ color: "#64748b", fontSize: 16 }}>{edaOpen === i ? "▲" : "▼"}</span>
                </div>
                {edaOpen === i && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #1e293b" }}>
                    <div style={{ ...S.tag("#34d399"), marginBottom: 12 }}>Business Insight</div>
                    <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>{q.insight}</p>
                    <div style={{ ...S.tag("#818cf8"), marginBottom: 8 }}>Visualization</div>
                    <p style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>{q.viz}</p>
                    <CodeBlock code={q.code} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ══════ ML ENGINE ══════ */}
        {active === "ml" && (
          <div>
            <h1 style={S.h1}>ML Engine</h1>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>
              XGBoost with Bayesian hyperparameter search, stratified CV, and SHAP explainability — the production standard.
            </p>

            {/* MODEL CARD */}
            <div style={{ ...S.card, borderLeft: "3px solid #34d399" }}>
              <h2 style={S.h2}>Model Selection Rationale</h2>
              <div style={S.grid2}>
                {[
                  { title: "Why XGBoost?", points: ["Native NaN handling", "scale_pos_weight for imbalance", "Tree pruning via gamma/alpha", "SHAP-compatible by design"] },
                  { title: "Why AUC-PR over ROC-AUC?", points: ["Class imbalance makes ROC optimistic", "Precision-Recall captures minority class", "Business cares about: Who to call?", "AP = area under PR curve"] },
                ].map((c, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 10 }}>{c.title}</div>
                    {c.points.map((p, j) => (
                      <div key={j} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <span style={{ color: "#34d399", fontSize: 12 }}>✓</span>
                        <span style={{ color: "#64748b", fontSize: 12 }}>{p}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <CodeBlock code={CODE_SNIPPETS.modeling} />

            {/* EXPECTED RESULTS */}
            <div style={S.card}>
              <h2 style={S.h2}>Expected Performance Targets</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {[
                  { metric: "ROC-AUC", target: "≥ 0.87", note: "Overall discriminatory power", color: "#818cf8" },
                  { metric: "AUC-PR", target: "≥ 0.61", note: "Precision vs Recall trade-off", color: "#34d399" },
                  { metric: "Recall@0.5", target: "≥ 0.72", note: "Catch 72%+ of actual churners", color: "#fb923c" },
                ].map((m, i) => (
                  <div key={i} style={{ ...S.card, margin: 0, textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.target}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#f1f5f9", margin: "4px 0" }}>{m.metric}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{m.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* SHAP EXPLANATION */}
            <div style={{ ...S.card, borderLeft: "3px solid #f43f5e" }}>
              <h2 style={S.h2}>SHAP: The "Senior" Differentiator</h2>
              <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.8, marginBottom: 16 }}>
                SHAP (SHapley Additive exPlanations) transforms a black-box model into an interpretable business tool.
                Each user gets a personal churn explanation: <em style={{ color: "#94a3b8" }}>"User #4821 is at 78% churn risk primarily due to high ticket_tenure_ratio (contributing +0.42 to churn log-odds) and low engagement_score (−0.31)."</em>
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {["shap.summary_plot()", "shap.force_plot()", "shap.dependence_plot()", "shap.waterfall_plot()"].map((f, i) => (
                  <div key={i} style={S.tag("#f43f5e")}>{f}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══════ BUSINESS REPORT ══════ */}
        {active === "report" && (
          <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 11, color: "#818cf8", fontWeight: 800, letterSpacing: 2 }}>EXECUTIVE REPORT</div>
                <h1 style={{ ...S.h1, margin: 0 }}>Churn Mitigation Strategy</h1>
              </div>
            </div>

            {/* KPI ROW */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 28 }}>
              {REPORT_METRICS.map((m, i) => (
                <div key={i} style={{ ...S.card, padding: 18 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: m.bad ? "#f43f5e" : "#34d399" }}>{m.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f1f5f9", margin: "4px 0" }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>{m.delta}</div>
                </div>
              ))}
            </div>

            {/* PROBLEM */}
            <div style={{ ...S.card, borderLeft: "3px solid #f43f5e" }}>
              <h2 style={{ ...S.h2, color: "#f43f5e" }}>① The Problem</h2>
              <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.8 }}>
                Monthly churn has risen from 12% to 15.2% over 6 months, driven primarily by <strong style={{ color: "#f1f5f9" }}>frustrated new users</strong> (ticket_tenure_ratio &gt; 0.8 in Month 1) and <strong style={{ color: "#f1f5f9" }}>disengaged mid-tenure users</strong> in the free tier.
                The top 20% of users by CLV represent $2.4M in projected annual revenue at risk.
              </p>
            </div>

            {/* KEY FINDINGS */}
            <div style={{ ...S.card, borderLeft: "3px solid #818cf8" }}>
              <h2 style={S.h2}>② Key Model Findings</h2>
              {[
                { rank: "#1", driver: "ticket_tenure_ratio", impact: "SHAP value: +0.42 per unit", insight: "Complaints in early tenure are 3× more predictive than late-tenure complaints." },
                { rank: "#2", driver: "engagement_score", impact: "SHAP value: −0.38 per unit", insight: "Below the 25th percentile = strong disengagement signal. Drop-off visible 8 weeks before churn." },
                { rank: "#3", driver: "fee_complaints", impact: "SHAP value: +0.29", insight: "68% of users who flagged fee complaints churned within 30 days without outreach." },
              ].map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "12px 0", borderBottom: "1px solid #1e293b" }}>
                  <div style={{ width: 28, fontSize: 20, fontWeight: 800, color: "#818cf8", flexShrink: 0 }}>{f.rank}</div>
                  <div>
                    <div style={{ fontFamily: "monospace", fontSize: 13, color: "#f1f5f9", fontWeight: 700 }}>{f.driver}</div>
                    <div style={{ fontSize: 11, color: "#818cf8", margin: "2px 0" }}>{f.impact}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{f.insight}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* INTERVENTION TABLE */}
            <div style={{ ...S.card, borderLeft: "3px solid #34d399" }}>
              <h2 style={S.h2}>③ Retention Intervention Framework</h2>
              {INTERVENTIONS.map((iv, i) => (
                <div key={i} style={{
                  background: "#060b14", borderRadius: 10, padding: 18, marginBottom: 12,
                  border: `1px solid ${iv.color}33`, borderLeft: `3px solid ${iv.color}`
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#f1f5f9" }}>{iv.segment}</span>
                    <div>
                      <span style={S.tag("#94a3b8")}>Cost: {iv.cost}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
                    <strong style={{ color: "#94a3b8" }}>Trigger:</strong>{" "}
                    <code style={{ color: iv.color }}>{iv.trigger}</code>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
                    <strong style={{ color: "#94a3b8" }}>Action:</strong> {iv.action}
                  </div>
                  <div style={{ fontSize: 11, color: "#34d399" }}>
                    <strong>Expected Save:</strong> {iv.expectedSave}
                  </div>
                </div>
              ))}
            </div>

            {/* PROJECTED IMPACT */}
            <div style={{ ...S.card, borderLeft: "3px solid #fb923c" }}>
              <h2 style={S.h2}>④ Projected Financial Impact</h2>
              <div style={S.grid2}>
                <div>
                  <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.7 }}>
                    If the model successfully reduces churn by <strong style={{ color: "#fb923c" }}>2 percentage points</strong> (15.2% → 13.2%) in the High-CLV segment:
                  </div>
                  {[
                    { label: "Retained users/month", value: "+100", color: "#34d399" },
                    { label: "Avg CLV recovered", value: "$1,840", color: "#818cf8" },
                    { label: "ARR uplift", value: "+$2.2M", color: "#fb923c" },
                    { label: "Intervention cost", value: "$54k/yr", color: "#f43f5e" },
                    { label: "Net ROI", value: "39.7×", color: "#34d399" },
                  ].map((r, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1e293b" }}>
                      <span style={{ fontSize: 13, color: "#64748b" }}>{r.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: r.color }}>{r.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ ...S.card, margin: 0, textAlign: "center", flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>Model Precision @ threshold 0.7</div>
                    <DonutChart pct={81} color="#34d399" label="Precision" />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>Of flagged users, 81% will actually churn</div>
                  </div>
                  <div style={{ ...S.card, margin: 0, background: "#0a2215", border: "1px solid #34d39944", padding: 16, textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "#34d399" }}>39.7×</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>Return on intervention spend</div>
                  </div>
                </div>
              </div>
            </div>

            {/* NEXT STEPS */}
            <div style={S.card}>
              <h2 style={S.h2}>⑤ Recommended Next Steps</h2>
              {[
                { step: "Deploy model to production via FastAPI endpoint — real-time scoring per user login.", tag: "Engineering" },
                { step: "A/B test the High-Touch intervention vs control group over 60 days.", tag: "Product" },
                { step: "Rebuild CLV using full BG/NBD model (lifetimes library) for greater accuracy.", tag: "Analytics" },
                { step: "Add NPS survey data as a feature — leading indicator that outperforms tickets.", tag: "Data" },
                { step: "Schedule monthly model retraining to prevent concept drift as product evolves.", tag: "MLOps" },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 14, padding: "10px 0", borderBottom: i < 4 ? "1px solid #1e293b" : "none" }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#818cf8", flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
                  <div>
                    <div style={{ ...S.tag("#6366f1"), marginBottom: 4 }}>{s.tag}</div>
                    <div style={{ fontSize: 13, color: "#94a3b8" }}>{s.step}</div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </main>
    </div>
  );
}
